import { PawPrint, Mountain } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { cn } from '@/lib/utils';
import { motion } from 'framer-motion';

export type ActivityFilters = {
  showAnimalActivities: boolean;
  showAdventureActivities: boolean;
};

interface ActivityTypeFilterProps {
  filters: ActivityFilters;
  onChange: (filters: ActivityFilters) => void;
}

export function ActivityTypeFilter({ filters, onChange }: ActivityTypeFilterProps) {
  const handleAnimalToggle = (checked: boolean) => {
    onChange({ ...filters, showAnimalActivities: checked });
  };

  const handleAdventureToggle = (checked: boolean) => {
    onChange({ ...filters, showAdventureActivities: checked });
  };

  // Ensure at least one filter is always on
  const isAnimalDisabled = !filters.showAdventureActivities && filters.showAnimalActivities;
  const isAdventureDisabled = !filters.showAnimalActivities && filters.showAdventureActivities;

  return (
    <motion.div 
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.2, duration: 0.3 }}
      className="w-full flex items-center justify-center mt-3"
    >
      <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2 bg-white rounded-full shadow-md p-1.5 px-3">
        <TooltipPrimitive.Provider delayDuration={300}>
          <TooltipPrimitive.Root>
            <TooltipPrimitive.Trigger asChild>
              <div
                onClick={() => !isAnimalDisabled && handleAnimalToggle(!filters.showAnimalActivities)}
                className={cn(
                  "h-8 rounded-full px-3 transition-all min-w-28 justify-between cursor-pointer flex items-center",
                  isAnimalDisabled && "cursor-not-allowed opacity-70",
                  filters.showAnimalActivities 
                    ? "bg-primary/10 text-primary hover:bg-primary/20" 
                    : "bg-transparent text-gray-500 hover:bg-gray-100"
                )}
              >
                <div className="flex items-center">
                  <PawPrint className="h-4 w-4 mr-2 flex-shrink-0" />
                  <span className="text-sm font-medium">Animals</span>
                </div>
                <motion.div 
                  initial={false}
                  animate={{ scale: filters.showAnimalActivities ? 1.05 : 1 }}
                  transition={{ duration: 0.2 }}
                  className="ml-2"
                  onClick={(e) => {
                    // Prevent click from propagating to parent
                    if (!isAnimalDisabled) {
                      e.stopPropagation();
                      handleAnimalToggle(!filters.showAnimalActivities);
                    }
                  }}
                >
                  <Switch
                    checked={filters.showAnimalActivities}
                    onCheckedChange={handleAnimalToggle}
                    disabled={isAnimalDisabled}
                    className="data-[state=checked]:bg-primary"
                  />
                </motion.div>
              </div>
            </TooltipPrimitive.Trigger>
            <TooltipPrimitive.Content 
              side="bottom" 
              sideOffset={5} 
              className="z-50 overflow-hidden rounded-md border bg-popover px-3 py-1.5 text-sm text-popover-foreground shadow-md"
            >
              <p className="text-xs">Show businesses with animal experiences</p>
            </TooltipPrimitive.Content>
          </TooltipPrimitive.Root>
        </TooltipPrimitive.Provider>

        <div className="hidden sm:block w-px h-6 self-center bg-gray-200 mx-1" />
        <div className="block sm:hidden w-full h-px bg-gray-200 my-1" />

        <TooltipPrimitive.Provider delayDuration={300}>
          <TooltipPrimitive.Root>
            <TooltipPrimitive.Trigger asChild>
              <div
                onClick={() => !isAdventureDisabled && handleAdventureToggle(!filters.showAdventureActivities)}
                className={cn(
                  "h-8 rounded-full px-3 transition-all min-w-28 justify-between cursor-pointer flex items-center",
                  isAdventureDisabled && "cursor-not-allowed opacity-70",
                  filters.showAdventureActivities 
                    ? "bg-primary/10 text-primary hover:bg-primary/20" 
                    : "bg-transparent text-gray-500 hover:bg-gray-100"
                )}
              >
                <div className="flex items-center">
                  <Mountain className="h-4 w-4 mr-2 flex-shrink-0" />
                  <span className="text-sm font-medium">Adventure</span>
                </div>
                <motion.div 
                  initial={false}
                  animate={{ scale: filters.showAdventureActivities ? 1.05 : 1 }}
                  transition={{ duration: 0.2 }}
                  className="ml-2"
                  onClick={(e) => {
                    // Prevent click from propagating to parent
                    if (!isAdventureDisabled) {
                      e.stopPropagation();
                      handleAdventureToggle(!filters.showAdventureActivities);
                    }
                  }}
                >
                  <Switch
                    checked={filters.showAdventureActivities}
                    onCheckedChange={handleAdventureToggle}
                    disabled={isAdventureDisabled}
                    className="data-[state=checked]:bg-primary"
                  />
                </motion.div>
              </div>
            </TooltipPrimitive.Trigger>
            <TooltipPrimitive.Content 
              side="bottom" 
              sideOffset={5} 
              className="z-50 overflow-hidden rounded-md border bg-popover px-3 py-1.5 text-sm text-popover-foreground shadow-md"
            >
              <p className="text-xs">Show businesses with adventure experiences</p>
            </TooltipPrimitive.Content>
          </TooltipPrimitive.Root>
        </TooltipPrimitive.Provider>
      </div>
    </motion.div>
  );
} 